import { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  ChevronLeft,
  ChevronRight,
  Brain,
  Heart,
  Zap,
  Users,
  HeartCrack,
  BookOpen,
  Mic,
  SendHorizontal,
  PanelLeft,
  BookHeart,
} from "lucide-react";
import { useTheme } from "next-themes";
import logo from "@/assets/logo.svg";
import logoDark from "@/assets/logo-dark.svg";

interface Message {
  role: "user" | "assistant";
  content: string;
}

interface TopicData {
  slug: string;
  title: string;
  description: string;
  buttonLabel: string;
  cta: string;
  icon: React.ComponentType<{ className?: string }>;
  conversation: Message[];
}

const topics: TopicData[] = [
  {
    slug: "ansiedade",
    title: "Sentindo ansiedade?",
    description:
      "Você não precisa enfrentar isso sem apoio. A KopfAI está aqui para te ajudar a aliviar esse sentimento, a qualquer momento.",
    buttonLabel: "Ansiedade",
    cta: "Conversar agora",
    icon: Brain,
    conversation: [
      {
        role: "user",
        content: "Estou me sentindo muito ansiosa esses últimos dias, não estou conseguindo focar no que devo fazer",
      },
      {
        role: "assistant",
        content: `Olá, Ana. Entendo o que você está sentindo. A ansiedade pode ser realmente desafiadora, especialmente quando afeta sua capacidade de focar e realizar suas tarefas.

Vamos investigar isso juntos. Para começar, gostaria de entender um pouco mais sobre o que está acontecendo.

Você consegue identificar alguma situação específica ou pensamento que desencadeia essa ansiedade e a dificuldade de concentração?`,
      },
    ],
  },
  {
    slug: "relacionamento",
    title: "Relacionamento te preocupando?",
    description:
      "Sentimentos confusos sobre quem a gente ama podem acabar tomando a nossa mente. A KopfAI te ajuda a aliviar isso e a entender melhor o que está acontecendo.",
    buttonLabel: "Relacionamento",
    cta: "Buscar clareza",
    icon: Users,
    conversation: [
      {
        role: "user",
        content: "Meu relacionamento está muito complicado, sinto que meu namorado está se afastando de mim",
      },
      {
        role: "assistant",
        content: `Olá, Bia. Entendo que essa situação deve ser bastante dolorosa e confusa. É natural sentir-se assim quando percebemos um afastamento em um relacionamento importante.

Vamos primeiro entender melhor o que está acontecendo e como você está interpretando essa situação.

Para começarmos, que tal explorarmos um pouco mais essa sensação de "afastamento"?

O que você observa que te faz pensar que ele está se afastando? (Pense em comportamentos específicos, não em sentimentos ou interpretações, por enquanto).

Quando você percebeu que isso começou a acontecer? Houve algum evento específico ou foi algo gradual?

Vamos juntos desvendar isso, Bia.`,
      },
    ],
  },
  {
    slug: "termino",
    title: "Dificuldade pós término?",
    description:
      "Um término dói. E dói de verdade. Às vezes mesmo tentando seguir em frente, a mente insiste em voltar. A KopfAI te ajuda a organizar os pensamentos e entender, com mais clareza, o que te fará bem agora.",
    buttonLabel: "Término",
    cta: "Conversar agora",
    icon: HeartCrack,
    conversation: [
      {
        role: "user",
        content:
          "Terminei meu relacionamento essa semana e estou me sentindo muito sozinho. Não sei como seguir em frente.",
      },
      {
        role: "assistant",
        content: `Olá, Oto. Sinto muito que esteja passando por este momento difícil. O término de um relacionamento pode trazer uma dor profunda e a sensação de solidão é algo muito comum e compreensível nessa situação.

É importante reconhecer e validar o que você está sentindo agora. Não há um jeito "certo" de se sentir depois de um término, e a solidão é uma emoção natural diante de uma grande mudança.

Podemos explorar juntos o que essa solidão significa para você e como podemos lidar com ela de uma forma construtiva para que esses sentimentos negativos passem e você fique bem. O que você acha?`,
      },
    ],
  },
  {
    slug: "estudos",
    title: "Pressão nos estudos?",
    description:
      "Provas e prazos podem pesar mais do que deveriam. Pra estudar com mais produtividade, a saúde mental também importa. A KopfAI te ajuda a focar nos estudos de uma forma mais leve.",
    buttonLabel: "Estudos",
    cta: "Conversar agora",
    icon: BookOpen,
    conversation: [
      {
        role: "user",
        content:
          "Tenho uma prova super importante na próxima semana e não consigo me concentrar, não consigo me desligar dos problemas...",
      },
      {
        role: "assistant",
        content: `Olá, Vic! Entendo perfeitamente como você se sente. É muito comum que a mente fique agitada e a concentração diminua quando estamos sob pressão, especialmente com algo importante como uma prova se aproximando, e ainda por cima com problemas pessoais pairando.

Vamos tentar focar em algumas estratégias que podem te ajudar a retomar o controle e a se concentrar. Que tal começarmos com o Modo Resolução de Problemas e um pouco de Autorregulação?

Primeiro, para te ajudar a "desligar" um pouco dos problemas, podemos tentar uma técnica simples de aterramento (grounding). Isso ajuda a trazer sua atenção para o presente e para o que você pode controlar agora.

1. Regra 3-3-3:
• Olhe ao seu redor e nomeie três coisas que você pode ver.
• Preste atenção e nomeie três sons que você pode ouvir.
• Mova três partes do seu corpo (por exemplo, mexa os dedos dos pés, os dedos das mãos, e a cabeça).

Como você se sente depois de tentar isso, Vic? Apenas um pouco mais presente?`,
      },
    ],
  },
  {
    slug: "desabafar",
    title: "Precisando desabafar?",
    description: "A KopfAI te ouve sem julgamentos, auxiliando no que for preciso para que você fique bem.",
    buttonLabel: "Desabafar",
    cta: "Começar conversa",
    icon: Heart,
    conversation: [
      {
        role: "user",
        content: "Hoje foi um dia horrível. Ninguém entende o que eu passo...",
      },
      {
        role: "assistant",
        content: `Olá, João. Sinto muito que seu dia tenha sido tão difícil. É compreensível que você sinta que ninguém consegue entender o que você está passando.

Essa sensação de isolamento ou de não ser compreendido pode ser muito pesada.

Para começarmos, você gostaria de compartilhar um pouco mais sobre o que tornou o dia tão horrível? Às vezes, apenas colocar em palavras pode aliviar um pouco.`,
      },
    ],
  },
  {
    slug: "estresse",
    title: "O dia a dia está pesando?",
    description:
      "Quando tudo parece demais, dar um passo de cada vez faz diferença. A KopfAI te ajuda a encontrar mais equilíbrio no dia a dia.",
    buttonLabel: "Estresse",
    cta: "Conversar agora",
    icon: Zap,
    conversation: [
      {
        role: "user",
        content: "Não estou suportando minha rotina, estou sobrecarregado e exausto.",
      },
      {
        role: "assistant",
        content: `Olá, Diego. Sinto muito que esteja passando por isso. É completamente compreensível se sentir sobrecarregado e exausto quando a rotina aperta.

Para começarmos a entender melhor essa situação, que tal explorarmos um pouco mais o que exatamente está contribuindo para essa sobrecarga?

Poderíamos tentar o MODO INVESTIGAÇÃO para identificar os principais gatilhos. O que você acha?`,
      },
    ],
  },
];

const topicSlugs = topics.map((t) => t.slug);

export function LandingAdSection() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { theme } = useTheme();

  const urlTopic = searchParams.get("topic");
  const initialIndex = urlTopic ? topicSlugs.indexOf(urlTopic) : 0;
  const [currentIndex, setCurrentIndex] = useState(initialIndex >= 0 ? initialIndex : 0);

  // Controle do carrossel mobile
  const carouselRef = useRef<HTMLDivElement>(null);
  const isUserInteractingRef = useRef(false);
  const autoScrollIntervalRef = useRef<number | null>(null);

  // Controle do scroll do mockup (efeito Apple)
  const mockupMessagesRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  const currentTopic = topics[currentIndex];
  const currentLogo = theme === "dark" ? logoDark : logo;

  useEffect(() => {
    const topic = searchParams.get("topic");
    if (topic && topicSlugs.includes(topic)) {
      setCurrentIndex(topicSlugs.indexOf(topic));

      // Track ad landing
      window.dataLayer?.push({
        event: "ad_landing",
        ad_topic: topic,
      });
    }
  }, [searchParams]);

  // Auto scroll para o carrossel mobile - VERSÃO SIMPLIFICADA PARA iOS
  useEffect(() => {
    const carousel = carouselRef.current;
    if (!carousel) return;

    const startAutoScroll = () => {
      if (autoScrollIntervalRef.current) {
        clearInterval(autoScrollIntervalRef.current);
      }

      autoScrollIntervalRef.current = window.setInterval(() => {
        if (!isUserInteractingRef.current && carousel) {
          const currentScroll = carousel.scrollLeft;
          const maxScroll = carousel.scrollWidth / 3;

          // Incremento pequeno para scroll suave
          const newScroll = currentScroll + 1;

          if (newScroll >= maxScroll) {
            carousel.scrollLeft = 0;
          } else {
            carousel.scrollLeft = newScroll;
          }
        }
      }, 20); // 50fps para suavidade
    };

    const handleTouchStart = () => {
      isUserInteractingRef.current = true;
      if (autoScrollIntervalRef.current) {
        clearInterval(autoScrollIntervalRef.current);
      }
    };

    const handleTouchEnd = () => {
      isUserInteractingRef.current = false;
      // Retoma o scroll após 2 segundos
      setTimeout(() => {
        if (!isUserInteractingRef.current) {
          startAutoScroll();
        }
      }, 2000);
    };

    const handleMouseEnter = () => {
      isUserInteractingRef.current = true;
      if (autoScrollIntervalRef.current) {
        clearInterval(autoScrollIntervalRef.current);
      }
    };

    const handleMouseLeave = () => {
      isUserInteractingRef.current = false;
      startAutoScroll();
    };

    // Adiciona listeners
    carousel.addEventListener("touchstart", handleTouchStart, { passive: true });
    carousel.addEventListener("touchend", handleTouchEnd, { passive: true });
    carousel.addEventListener("mouseenter", handleMouseEnter);
    carousel.addEventListener("mouseleave", handleMouseLeave);

    // Inicia o auto scroll
    startAutoScroll();

    return () => {
      if (autoScrollIntervalRef.current) {
        clearInterval(autoScrollIntervalRef.current);
      }
      carousel.removeEventListener("touchstart", handleTouchStart);
      carousel.removeEventListener("touchend", handleTouchEnd);
      carousel.removeEventListener("mouseenter", handleMouseEnter);
      carousel.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, []);

  // Efeito de scroll do mockup estilo Apple
  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current || !mockupMessagesRef.current) return;

      const section = sectionRef.current;
      const messagesContainer = mockupMessagesRef.current;

      const rect = section.getBoundingClientRect();
      const sectionHeight = section.offsetHeight;

      // Ajuste no cálculo de progresso para tornar mais responsivo no mobile
      const progress = Math.max(0, Math.min(1, -rect.top / (sectionHeight - window.innerHeight)));

      const maxScroll = messagesContainer.scrollHeight - messagesContainer.clientHeight;

      // Usa requestAnimationFrame para scroll mais suave no iOS
      requestAnimationFrame(() => {
        messagesContainer.scrollTop = progress * maxScroll;
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, [currentIndex]);

  const goToTopic = (index: number) => {
    setCurrentIndex(index);
    // Scroll para o topo da seção
    if (sectionRef.current) {
      sectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const goToPrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? topics.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev === topics.length - 1 ? 0 : prev + 1));
  };

  const handleCTA = () => {
    navigate("/auth?tab=signup");
  };

  return (
    <section id="ad-section" ref={sectionRef} className="relative h-[300vh] scroll-mt-[60px]">
      {/* Conteúdo fixo que fica preso enquanto scrolla */}
      <div className="sticky top-0 min-h-screen flex items-center py-12 md:py-16 lg:py-24 bg-gradient-to-b from-background to-muted/30 overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary/5 rounded-full blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-accent/10 rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto px-4 max-w-6xl relative w-full">
          {/* Main content grid - text left, mockup right on desktop */}
          <div className="grid md:grid-cols-2 gap-6 md:gap-12 items-center mb-8 md:mb-12">
            {/* Left side - Text content (title only on mobile, full on desktop) */}
            <div className="text-center md:text-left order-1 md:order-1">
              {/* Title */}
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-primary mb-3 md:mb-4 leading-tight">
                {currentTopic.title}
              </h2>

              {/* Description - hidden on mobile, visible on desktop */}
              <p className="hidden md:block text-base md:text-lg lg:text-xl text-muted-foreground mb-6 md:mb-8 leading-relaxed">
                {currentTopic.description}
              </p>

              {/* CTA - visible on desktop */}
              <div className="hidden md:block">
                <Button
                  onClick={handleCTA}
                  size="lg"
                  className="px-8 py-6 text-lg font-semibold rounded-full shadow-lg hover:shadow-xl transition-all bg-primary hover:bg-primary/90"
                >
                  {currentTopic.cta}
                </Button>
                <p className="text-muted-foreground text-sm mt-3">7 dias grátis • Sem cartão de crédito</p>
              </div>
            </div>

            {/* Right side - Chat Mockup */}
            <div className="flex justify-center order-2 md:order-2">
              <div className="relative w-full max-w-[320px] md:max-w-[340px]">
                {/* Phone Frame */}
                <div className="bg-foreground/5 rounded-[2.5rem] p-2 shadow-2xl border border-border/50 backdrop-blur-sm">
                  {/* Phone Notch */}
                  <div className="absolute top-4 left-1/2 -translate-x-1/2 w-20 h-5 bg-foreground/10 rounded-full z-10" />

                  {/* Screen Container */}
                  <div className="bg-background rounded-[2.2rem] overflow-hidden shadow-inner">
                    {/* Chat Header */}
                    <div className="border-b border-border bg-card shadow-sm px-4 py-3 pt-8 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="icon" className="h-9 w-9">
                          <PanelLeft className="h-5 w-5" />
                        </Button>
                        <img src={currentLogo} alt="KopfAI" className="h-5" />
                      </div>

                      <Button variant="ghost" size="icon" className="rounded-full border border-current h-8 w-8">
                        <BookHeart className="h-4 w-4" />
                      </Button>
                    </div>

                    {/* Chat Messages */}
                    <div
                      ref={mockupMessagesRef}
                      className="p-4 space-y-4 min-h-[380px] max-h-[380px] overflow-hidden bg-background"
                    >
                      {currentTopic.conversation.map((message, idx) => (
                        <div key={idx} className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}>
                          <div
                            className={`max-w-[82%] px-4 py-3 text-sm leading-relaxed whitespace-pre-line ${
                              message.role === "user"
                                ? "bg-blue-50 dark:bg-blue-950/30 text-foreground rounded-2xl rounded-tr-sm"
                                : "bg-muted text-foreground rounded-2xl rounded-tl-sm"
                            }`}
                          >
                            {message.content}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Chat Input */}
                    <div className="px-4 pb-6 pt-2 bg-background">
                      <div className="flex items-center gap-3 bg-background border border-border rounded-full px-4 py-3 shadow-sm">
                        <button className="flex-shrink-0">
                          <Mic className="w-5 h-5 text-muted-foreground" />
                        </button>
                        <span className="text-muted-foreground text-sm flex-1">Digite sua mensagem...</span>
                        <button className="flex-shrink-0 w-9 h-9 rounded-full bg-yellow-300 flex items-center justify-center hover:bg-yellow-400 transition-colors">
                          <SendHorizontal className="w-4 h-4 text-gray-700" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Description - visible only on mobile, below mockup */}
            <div className="md:hidden text-center order-3">
              <p className="text-base text-muted-foreground leading-relaxed">{currentTopic.description}</p>
            </div>
          </div>

          {/* Topic Navigation Pills */}
          <div className="mb-6 md:mb-8">
            {/* Desktop: Pills estáticos sem cortes */}
            <div className="hidden md:flex flex-wrap items-center justify-center gap-2 px-4">
              {topics.map((topic, idx) => {
                const Icon = topic.icon;
                const isActive = idx === currentIndex;
                return (
                  <button
                    key={topic.slug}
                    onClick={() => goToTopic(idx)}
                    className={`flex items-center gap-2 whitespace-nowrap px-4 py-2.5 rounded-full text-sm font-medium transition-all duration-200 shadow-sm ${
                      isActive
                        ? "bg-primary text-primary-foreground shadow-md scale-105"
                        : "bg-background border border-border text-foreground hover:bg-muted hover:border-primary/30"
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {topic.buttonLabel}
                  </button>
                );
              })}
            </div>

            {/* Mobile: Infinite carousel */}
            <div className="md:hidden relative overflow-hidden">
              <div
                ref={carouselRef}
                className="flex gap-2 py-2 overflow-x-scroll"
                style={{
                  msOverflowStyle: "none",
                  scrollbarWidth: "none",
                  WebkitOverflowScrolling: "touch",
                  scrollSnapType: "none",
                }}
              >
                {/* Triplicamos os tópicos para criar o efeito infinito */}
                {[...topics, ...topics, ...topics].map((topic, idx) => {
                  const Icon = topic.icon;
                  const actualIndex = idx % topics.length;
                  const isActive = actualIndex === currentIndex;
                  return (
                    <button
                      key={`${topic.slug}-${idx}`}
                      onClick={() => {
                        goToTopic(actualIndex);
                        isUserInteractingRef.current = true;
                        if (autoScrollIntervalRef.current) {
                          clearInterval(autoScrollIntervalRef.current);
                        }
                        setTimeout(() => {
                          isUserInteractingRef.current = false;
                        }, 3000);
                      }}
                      className={`flex items-center gap-2 whitespace-nowrap px-4 py-2.5 rounded-full text-sm font-medium transition-all duration-200 shadow-sm flex-shrink-0 ${
                        isActive
                          ? "bg-primary text-primary-foreground shadow-md scale-105"
                          : "bg-background border border-border text-foreground hover:bg-muted hover:border-primary/30"
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {topic.buttonLabel}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* CTA - visible on mobile only */}
          <div className="text-center md:hidden mt-6">
            <Button
              onClick={handleCTA}
              size="lg"
              className="w-full max-w-sm px-8 py-6 text-lg font-semibold rounded-full shadow-lg hover:shadow-xl transition-all bg-primary hover:bg-primary/90"
            >
              {currentTopic.cta}
            </Button>
            <p className="text-muted-foreground text-sm mt-3">7 dias grátis • Sem cartão de crédito</p>
          </div>

          {/* Destaque Questionário Inicial */}
          <div className="mt-12 md:mt-16 max-w-2xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/20 text-accent-foreground mb-4">
              <span className="text-sm font-medium">Dica</span>
            </div>
            <h3 className="text-lg md:text-xl font-semibold text-foreground mb-2">
              Não sabe por onde começar sua conversa?
            </h3>
            <p className="text-sm md:text-base text-muted-foreground">
              A KopfAI oferece um questionário inicial para guiar sua jornada de bem-estar mental.
            </p>
          </div>
        </div>

        <style>{`
          div[style*="msOverflowStyle"]::-webkit-scrollbar {
            display: none;
          }
        `}</style>
      </div>
    </section>
  );
}
